import { IconButton, LuIcon, toast, useAlertDialog } from "@overmap-ai/blocks"
import { type EmailDomain, OrganizationAccessLevel } from "@overmap-ai/common"
import { APIError, selectActiveOrganizationAccess } from "@overmap-ai/core"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { sdk } from "../../../sdk"
import { useAppSelector } from "../../../store"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useEmailDomainTableColumns = () => {
	return useMemo<ColumnDef<EmailDomain>[]>(
		() => [
			{
				id: "emailDomain",
				accessorKey: "domain",
				enableHiding: false,
				meta: {
					plainHeader: "Domain",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Domain
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
			},
			{
				id: "removeDomain",
				enableSorting: false,
				enableHiding: false,
				cell: function Cell({ row }) {
					const openAlertDialog = useAlertDialog()
					const activeOrganizationAccess = useAppSelector(selectActiveOrganizationAccess)

					const removeDomain = useCallback((emailDomain: EmailDomain) => {
						sdk.emailDomains.delete(emailDomain).catch((error: APIError) => {
							if (error instanceof APIError) {
								toast.error({ title: "Unable to remove email domain.", description: error.message })
							} else {
								toast.error({ title: "An unknown error occurred", description: "" })
							}
						})
					}, [])

					const handleRemoveClick = useCallback(() => {
						openAlertDialog({
							title: "Remove email domain",
							description:
								"This will remove the email domain from the organization. You can add it back at any time.",
							size: "sm",
							onAction: () => removeDomain(row.original),
							action: "Remove",
						})
					}, [openAlertDialog, removeDomain, row.original])

					return (
						<IconButton
							onClick={handleRemoveClick}
							disabled={activeOrganizationAccess?.access_level !== OrganizationAccessLevel.ADMIN}
							aria-label="Remove email domain"
							variant="surface"
						>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					)
				},
			},
		],
		[],
	)
}

export const useEmailDomainTable = (args: Omit<TableOptions<EmailDomain>, "getCoreRowModel">) => {
	return useReactTable<EmailDomain>({
		...args,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
